import { buildDateFilterSpecs, formatDateInput } from "./filterSpecUtils";
import { resolveV3Config } from "./submissionStatusSpec";
import * as aggregates from "common/aggregates";
import * as filters from "common/filters";
import * as formulae from "common/formulae";
import * as logger from "common/logger";
import {
  currencyFormat,
  dpString,
  isFiniteNumber,
  percentage,
} from "common/numbers";

/**
 * Review-tab scorecard: a small grid of configured buckets (rows) by measures
 * (columns) over a calendar month of review-list rows, with subtotal, total and
 * year-to-date rows and a per-company plan to compare against.
 *
 * Everything here is pure. The component feeds it the config, the rows of two
 * queries (month, YTD) and the plan document; it gets back a table to render.
 *
 * Config lives in `submission_review_config.scorecard` (a `v3` block overrides,
 * like every other Review-tab key):
 *
 *   {
 *     periodColumn: "inception",
 *     buckets:   [{ key, label, filters: [{ key, operator, value }] }],
 *     subtotals: [{ label, buckets: [bucketKey] }],
 *     columns:   [ aggregate | plan | formula ],
 *     ytd: true
 *   }
 *
 * Column kinds:
 *   aggregate  { key, label, method, column, filters?, format?, dp? }
 *   plan       { key, label, plan: measure, format?, dp? }
 *   formula    { key, label, formula: { method, a, b }, format?, dp? }
 *              a / b are the key of an EARLIER column, or a number.
 *
 * Plan document (`review_scorecard_plan`, see PLAN_CONFIG_KEY):
 *   { periods: { "YYYY-MM": { [bucketKey]: { [measure]: number } } } }
 */

export const PLAN_CONFIG_KEY = "review_scorecard_plan";

export const SCORECARD_ROW_KIND = {
  BUCKET: "bucket",
  SUBTOTAL: "subtotal",
  TOTAL: "total",
  YTD: "ytd",
};

export const SCORECARD_FORMAT = {
  INTEGER: "integer",
  CURRENCY: "currency",
  PERCENTAGE: "percentage",
  NUMBER: "number",
};

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFAULT_PERIOD_COLUMN = "inception";
// Always fetched: counts are of accounts, and the period column is what the
// two queries filter on.
const ALWAYS_COLUMNS = ["submissionId"];
// Intl.NumberFormat's range for fraction digits.
const MAX_DP = 20;

const isObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

const isNonEmptyString = (value) =>
  typeof value === "string" && value.length > 0;

const logDrop = (what, reason) =>
  logger.error(`Review scorecard: dropped ${what}: ${reason}`);

// `common/filters` entries: { key, operator?, value }. Operator defaults to
// EQUAL there, so only reject one that is present and unknown. A filter on a
// column that is not on the table is the same failure as a malformed filter
// (EQUAL matches nothing, NOT_EQUAL everything, neither looks wrong at a
// glance), so it fails validation too.
const validFilters = (list, knownColumnNames) => {
  if (list === undefined) return true;
  if (!Array.isArray(list)) return false;
  return list.every(
    (f) =>
      isObject(f) &&
      isNonEmptyString(f.key) &&
      (f.operator === undefined ||
        Object.values(filters.OPERATOR).includes(f.operator)) &&
      f.hasOwnProperty("value") &&
      knownColumnNames.has(f.key)
  );
};

const columnKind = (column) => {
  if (!isObject(column)) return null;
  if (column.formula !== undefined) return "formula";
  if (column.plan !== undefined) return "plan";
  if (column.method !== undefined) return "aggregate";
  return null;
};

const validFormat = (format) =>
  format === undefined || Object.values(SCORECARD_FORMAT).includes(format);

const validDp = (dp) =>
  dp === undefined || (Number.isInteger(dp) && dp >= 0 && dp <= MAX_DP);

// `earlierKeys` are the keys of the columns kept so far, in config order: a
// formula may only read those, so it can always be evaluated.
const validColumn = (column, knownColumnNames, earlierKeys) => {
  if (!isNonEmptyString(column?.key) || !isNonEmptyString(column?.label)) {
    return "needs a key and a label";
  }
  if (!validFormat(column.format)) return `unknown format "${column.format}"`;
  if (!validDp(column.dp)) return `dp must be a whole number 0-${MAX_DP}`;
  switch (columnKind(column)) {
    case "aggregate":
      if (!aggregates.isMethod(column.method)) {
        return `unknown aggregate method "${column.method}"`;
      }
      if (!isNonEmptyString(column.column)) return "aggregate needs a column";
      if (!knownColumnNames.has(column.column)) {
        return `"${column.column}" is not a column of the table`;
      }
      if (!validFilters(column.filters, knownColumnNames)) {
        return "invalid filters";
      }
      return null;
    case "plan":
      return isNonEmptyString(column.plan) ? null : "plan needs a measure name";
    case "formula": {
      const f = column.formula;
      if (!isObject(f) || !Object.values(formulae.METHOD).includes(f.method)) {
        return "formula needs one of ADD, SUBTRACT, MULTIPLY, DIVIDE";
      }
      const operandOk = (operand) =>
        typeof operand === "number" ||
        (isNonEmptyString(operand) && earlierKeys.has(operand));
      return operandOk(f.a) && operandOk(f.b)
        ? null
        : "formula operands must be earlier column keys or numbers";
    }
    default:
      return "needs one of method, plan or formula";
  }
};

/**
 * Resolve and validate the scorecard block of a company's
 * submission_review_config against the table's real column names (a Set).
 * Returns null when there is nothing usable, so the caller renders nothing.
 * Invalid buckets, subtotals and columns are dropped and logged rather than
 * rendered with a wrong number; a formula whose operand was dropped goes too.
 */
export const resolveScorecardConfig = (
  submissionReviewConfig,
  knownColumnNames
) => {
  const raw = resolveV3Config(submissionReviewConfig)?.scorecard;
  if (!isObject(raw)) return null;
  if (!(knownColumnNames instanceof Set) || knownColumnNames.size === 0) {
    logDrop("scorecard", "the table's column names are not known");
    return null;
  }

  const periodColumn = isNonEmptyString(raw.periodColumn)
    ? raw.periodColumn
    : DEFAULT_PERIOD_COLUMN;
  if (!knownColumnNames.has(periodColumn)) {
    logDrop("scorecard", `period column "${periodColumn}" is not a column`);
    return null;
  }

  const buckets = (Array.isArray(raw.buckets) ? raw.buckets : []).filter(
    (b) => {
      const ok =
        isObject(b) &&
        isNonEmptyString(b.key) &&
        isNonEmptyString(b.label) &&
        Array.isArray(b.filters) &&
        b.filters.length > 0 &&
        validFilters(b.filters, knownColumnNames);
      if (!ok) logDrop(`bucket ${JSON.stringify(b?.key)}`, "invalid");
      return ok;
    }
  );
  const bucketKeys = new Set(buckets.map((b) => b.key));
  if (bucketKeys.size !== buckets.length) {
    logDrop("scorecard", "bucket keys must be unique");
    return null;
  }

  const keptKeys = new Set();
  const columns = [];
  (Array.isArray(raw.columns) ? raw.columns : []).forEach((c) => {
    const problem =
      validColumn(c, knownColumnNames, keptKeys) ??
      (keptKeys.has(c.key) ? "column keys must be unique" : null);
    if (problem) {
      logDrop(`column ${JSON.stringify(c?.key)}`, problem);
      return;
    }
    keptKeys.add(c.key);
    columns.push(c);
  });

  const subtotals = (Array.isArray(raw.subtotals) ? raw.subtotals : []).filter(
    (s) => {
      const ok =
        isObject(s) &&
        isNonEmptyString(s.label) &&
        Array.isArray(s.buckets) &&
        s.buckets.length > 0 &&
        s.buckets.every((k) => bucketKeys.has(k));
      if (!ok) logDrop(`subtotal ${JSON.stringify(s?.label)}`, "invalid");
      return ok;
    }
  );

  if (buckets.length === 0 || columns.length === 0) {
    logDrop("scorecard", "no usable buckets or columns");
    return null;
  }

  return {
    periodColumn,
    buckets,
    subtotals,
    columns,
    ytd: raw.ytd !== false,
  };
};

/** Every review_list column the scorecard reads, for the query spec. */
export const scorecardColumnNames = (config) => {
  const names = new Set([...ALWAYS_COLUMNS, config.periodColumn]);
  config.buckets.forEach((b) => b.filters.forEach((f) => names.add(f.key)));
  config.columns.forEach((c) => {
    if (columnKind(c) !== "aggregate") return;
    names.add(c.column);
    (c.filters ?? []).forEach((f) => names.add(f.key));
  });
  return [...names];
};

// ---- periods -------------------------------------------------------------

const pad2 = (n) => String(n).padStart(2, "0");

/** "YYYY-MM" for a 1-based month. */
export const periodKey = (year, month) => `${year}-${pad2(month)}`;

/** "July 2026". */
export const periodLabel = ({ year, month }) =>
  `${MONTH_NAMES[month - 1]} ${year}`;

const lastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

/** { from, to } as "YYYY-MM-DD" strings covering the whole calendar month. */
export const monthRange = (year, month) => ({
  from: `${year}-${pad2(month)}-01`,
  to: `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`,
});

/** 1 January to the end of the given month. */
export const ytdRange = (year, month) => ({
  from: `${year}-01-01`,
  to: monthRange(year, month).to,
});

/** The "YYYY-MM" keys from January to the given month, inclusive. */
export const ytdPeriodKeys = (year, month) =>
  Array.from({ length: month }, (_, i) => periodKey(year, i + 1));

// A filter bound is a local Date from the panel or a "YYYY-MM-DD" string from
// company config; either way we want the local calendar date.
const toYmd = (value) => {
  if (value instanceof Date) return formatDateInput(value);
  return typeof value === "string" ? value.slice(0, 10) : null;
};

/**
 * If a date-filter range ({ from, to }, Dates or "YYYY-MM-DD" strings) spans
 * exactly one calendar month, return { year, month }; otherwise null. Lets the
 * panel open on the month the grid is already filtered to.
 */
export const periodFromDateRange = (range) => {
  const from = toYmd(range?.from);
  const to = toYmd(range?.to);
  if (!from || !to) return null;
  const match = /^(\d{4})-(\d{2})-01$/.exec(from);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return to === monthRange(year, month).to ? { year, month } : null;
};

/** The current calendar month, injectable for tests. */
export const currentPeriod = (today = new Date()) => ({
  year: today.getFullYear(),
  month: today.getMonth() + 1,
});

export const shiftPeriod = ({ year, month }, delta) => {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};

// ---- query ---------------------------------------------------------------

/**
 * A minimal SINGLE_TABLE_REPORT specification over review_list: the columns the
 * scorecard reads, filtered to one date range on the period column. Rows come
 * back with internal enum keys, which is what the config filters compare to.
 */
export const buildScorecardSpecification = (
  config,
  range,
  displayNameMap = {},
  tableName = "review_list"
) => {
  const columns = scorecardColumnNames(config);
  return {
    sourceTables: [tableName],
    sourceColumns: columns.map((column) => ({
      sourceTable: tableName,
      sourceColumn: column,
      column,
      displayName: displayNameMap[column] ?? column,
    })),
    outputColumns: columns.map((column) => ({
      column,
      displayName: displayNameMap[column] ?? column,
    })),
    filters: buildDateFilterSpecs(config.periodColumn, range),
    order: [{ column: config.periodColumn, direction: "ASC" }],
    aggregate: null,
    joins: null,
    group: null,
  };
};

// ---- computation ---------------------------------------------------------

// Plan figures are numbers, but a hand-edited document may carry "12114803".
const planValue = (plan, periodKeys, bucketKeys, measure) => {
  const periods = plan?.periods;
  if (!isObject(periods)) return 0;
  let total = 0;
  periodKeys.forEach((p) => {
    bucketKeys.forEach((b) => {
      const value = periods[p]?.[b]?.[measure];
      if (isFiniteNumber(value)) total += Number(value);
    });
  });
  return total;
};

const finiteOrNull = (value) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * Compute one scorecard row's values. `rows` are the review-list rows this row
 * aggregates over (already narrowed to its bucket(s)); `periodKeys` and
 * `bucketKeys` locate its plan figures. Formula columns read the values of
 * earlier columns (validation guarantees they exist); a non-finite result,
 * e.g. a divide by zero, is null and renders blank.
 */
const computeValues = (config, rows, plan, periodKeys, bucketKeys, context) => {
  const values = {};
  config.columns.forEach((column) => {
    switch (columnKind(column)) {
      case "aggregate": {
        const narrowed = column.filters?.length
          ? filters.apply(column.filters, rows, context)
          : rows;
        // Nothing to aggregate (or every value null) is a real zero, not an
        // error; aggregatedByKey would log the latter case.
        values[column.key] = narrowed.some(
          (row) => row?.[column.column] != null
        )
          ? aggregates.aggregatedByKey(
              column.method,
              column.column,
              narrowed,
              context
            )
          : 0;
        break;
      }
      case "plan":
        values[column.key] = planValue(
          plan,
          periodKeys,
          bucketKeys,
          column.plan
        );
        break;
      case "formula": {
        const { method, a, b } = column.formula;
        const operand = (op) =>
          typeof op === "number" ? op : values[op] ?? null;
        const left = operand(a);
        const right = operand(b);
        values[column.key] =
          left == null || right == null
            ? null
            : finiteOrNull(formulae.calculate(method, left, right, context));
        break;
      }
      default:
        values[column.key] = null;
    }
  });
  return values;
};

// Union of several buckets' row lists, each row once. Rows are the parsed
// objects of one query result, so object identity is row identity.
const unionRows = (lists) => {
  const seen = new Set();
  const out = [];
  lists.forEach((list) =>
    list.forEach((row) => {
      if (seen.has(row)) return;
      seen.add(row);
      out.push(row);
    })
  );
  return out;
};

/**
 * Build the scorecard table.
 *
 *   config    resolved by resolveScorecardConfig
 *   rows      review-list rows for the selected month
 *   ytdRows   review-list rows for 1 Jan .. end of the selected month (or null)
 *   plan      the review_scorecard_plan document (or null)
 *   year, month  the selected period
 *
 * Returns { columns, rows: [{ key, kind, label, values }] } where values is
 * keyed by column key. Bucket rows come first in config order, each subtotal
 * follows the last bucket it includes, then the total, then YTD.
 */
export const computeScorecard = ({
  config,
  rows,
  ytdRows = null,
  plan = null,
  year,
  month,
}) => {
  const context = "review scorecard";
  const monthKeys = [periodKey(year, month)];
  const allBucketKeys = config.buckets.map((b) => b.key);

  const bucketRows = {};
  config.buckets.forEach((bucket) => {
    bucketRows[bucket.key] = filters.apply(bucket.filters, rows ?? [], context);
  });

  const out = [];
  config.buckets.forEach((bucket, index) => {
    out.push({
      key: bucket.key,
      kind: SCORECARD_ROW_KIND.BUCKET,
      label: bucket.label,
      values: computeValues(
        config,
        bucketRows[bucket.key],
        plan,
        monthKeys,
        [bucket.key],
        context
      ),
    });
    // A subtotal sits under the last bucket it includes.
    config.subtotals.forEach((subtotal, sIndex) => {
      const lastMember = Math.max(
        ...subtotal.buckets.map((k) => allBucketKeys.indexOf(k))
      );
      if (lastMember !== index) return;
      out.push({
        key: `subtotal-${sIndex}`,
        kind: SCORECARD_ROW_KIND.SUBTOTAL,
        label: subtotal.label,
        values: computeValues(
          config,
          unionRows(subtotal.buckets.map((k) => bucketRows[k])),
          plan,
          monthKeys,
          subtotal.buckets,
          context
        ),
      });
    });
  });

  out.push({
    key: "total",
    kind: SCORECARD_ROW_KIND.TOTAL,
    label: "Total",
    values: computeValues(
      config,
      unionRows(allBucketKeys.map((k) => bucketRows[k])),
      plan,
      monthKeys,
      allBucketKeys,
      context
    ),
  });

  if (config.ytd && Array.isArray(ytdRows)) {
    const ytdBucketRows = config.buckets.map((bucket) =>
      filters.apply(bucket.filters, ytdRows, context)
    );
    out.push({
      key: "ytd",
      kind: SCORECARD_ROW_KIND.YTD,
      label: "Year to Date",
      values: computeValues(
        config,
        unionRows(ytdBucketRows),
        plan,
        ytdPeriodKeys(year, month),
        allBucketKeys,
        context
      ),
    });
  }

  return { columns: config.columns, rows: out };
};

// ---- display -------------------------------------------------------------

/** Format a computed cell for display; null/non-finite values render blank. */
export const formatScorecardValue = (column, value) => {
  if (!isFiniteNumber(value)) return "";
  const dp = Number.isInteger(column.dp) ? column.dp : undefined;
  switch (column.format) {
    case SCORECARD_FORMAT.CURRENCY:
      return currencyFormat(value, dp ?? 0);
    case SCORECARD_FORMAT.PERCENTAGE:
      return percentage(dp ?? 0)(value);
    case SCORECARD_FORMAT.INTEGER:
      return dpString(0)(Math.round(value));
    default:
      return dpString(dp ?? 2, { minDp: dp ?? 0 })(value);
  }
};
